import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { BusinessAssetDTO } from '../../tables/Business/Business.meta';
import {
  BusinessAssetsFormValues,
  dataToForm,
  FORM_SCHEMA,
  formToBody,
  UpsertBusinessAssetDTO,
} from './BusinessAssetsForm.meta';
import {
  ADD_ASSET_SUCCESS_MESSAGE,
  CLONE_ASSET_SUCCESS_MESSAGE,
  EDIT_ASSET_SUCCESS_MESSAGE,
} from '../../OtherAssets.meta';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { useUpsertBusinessAssetsMutation } from '../../api/useUpsertBusinessAssetsMutation';
import { OtherAssetsDTO } from '../../api/useOtherAssetsQuery';
import {
  EXCLUDE_OPT,
  getAssetBucketOptions,
} from '../components/AssetBucketSelect/AssetBucketSelect.meta';
import { onManagementSelect as baseOnManagementSelect } from '../components/ManagementStyle/ManagementStyle.meta';
import { selectCurrentClient } from '../../../../CommonSelectors/CommonAreaSelector';

const FIELDS_TO_SHOW_ADVANCED = [
  'SaleYear',
  'IsSoldAtRetirement',
  'IncomeGrowth',
  'SelfEmployed',
  'UseUnderlyingAssetsForValue',
];

interface Params {
  clientId: string;
  asset: BusinessAssetDTO | null;
  options: OtherAssetsDTO['Options'];
  onSuccess: () => void;
  readOnly?: boolean;
}

export const useBusinessAssetsForm = ({ clientId, asset, options, onSuccess, readOnly }: Params) => {
  const formBag = useForm<BusinessAssetsFormValues>({
    defaultValues: dataToForm({ data: asset, options }),
    resolver: yupResolver(FORM_SCHEMA),
    disabled: readOnly,
  });

  const { Dob: primaryDob, PartnerDob: spouseDob } = useSelector(selectCurrentClient);

  const primaryYob = primaryDob ? new Date(primaryDob).getFullYear() : null;
  const spouseYob = spouseDob ? new Date(spouseDob).getFullYear() : null;

  const [ownerModel, managementStyleSelection] = formBag.watch([
    'ownerModel',
    'orionManagementStyleId',
  ]);

  const mutationBag = useUpsertBusinessAssetsMutation({ clientId });

  const ownerOptions = [
    {
      label: 'People',
      options: options.BusinessAssetOptions.ownerOptions.People.map(owner => ({
        value: owner.Name,
        label: owner.Name,
      })),
    },
    {
      label: 'Trusts',
      options: options.BusinessAssetOptions.ownerOptions.Trusts.map(owner => ({
        value: owner.Name,
        label: owner.Name,
      })),
    },
  ];

  const isPrincipal =
    ownerModel.length === 1 &&
    ownerModel[0].Name === options.BusinessAssetOptions.ownerOptions.People[0].Name;
  const isSpouse =
    ownerModel.length === 1 &&
    ownerModel[0].Name === options.BusinessAssetOptions.ownerOptions.People[1]?.Name;
  const isJoint = ownerModel.length > 1 || ownerModel.some(owner => owner.Name === 'Joint');

  const saleYearOptions = options.BusinessAssetOptions.SaleYearOptions.map(year => {
    const yearNumber = Number(year);

    let rightLabel: string | undefined;

    if (Number.isInteger(yearNumber)) {
      const getAgeLabel = (birthYear: number) => `Age ${yearNumber - birthYear}`;

      if (isJoint && primaryYob) {
        rightLabel = `${getAgeLabel(primaryYob)}${spouseYob ? `, ${getAgeLabel(spouseYob)}` : ''}`;
      } else if (isPrincipal && primaryYob) {
        rightLabel = getAgeLabel(primaryYob);
      } else if (isSpouse && spouseYob) {
        rightLabel = getAgeLabel(spouseYob);
      }
    }

    return {
      value: year,
      label: year,
      rightLabel,
    };
  });

  const defaultShowAdvanced =
    (!!asset && FIELDS_TO_SHOW_ADVANCED.some(field => !!asset[field] || asset[field] === 0)) ||
    asset?.BusinessSaleSchedule?.some(
      ({ SaleAmount, NetWorthPercent }) => SaleAmount !== null || NetWorthPercent !== null,
    );

  const baseAssetBucketOptions = [
    ...options.AvailableAssetBuckets.map(({ Title }, index) => ({
      label: Title,
      value: index,
      rightLabel:
        index === options.BusinessAssetOptions.DefaultAssetBucketIndex ? 'Default' : undefined,
    })),
    EXCLUDE_OPT,
  ];

  const onSubmit = formBag.handleSubmit(async (values: BusinessAssetsFormValues) => {
    const body: UpsertBusinessAssetDTO = {
      clientId,
      Id: (!asset?.isClone && asset?.Id) || null,
      ...formToBody(values),
    };

    try {
      await mutationBag.mutateAsync(body);

      toast.success(
        asset?.Id
          ? asset.isClone
            ? CLONE_ASSET_SUCCESS_MESSAGE
            : EDIT_ASSET_SUCCESS_MESSAGE
          : ADD_ASSET_SUCCESS_MESSAGE,
      );

      onSuccess();
    } catch (error) {
      console.error(error);
    }
  });

  const {
    AssetBucketingByManagementStyle: assetBucketingByManagementStyle,
    ManagementStyles,
    DisplayAssetBucketing: displayAssetBucketing,
  } = options;

  const managementStylesOptions = ManagementStyles.map(option => ({
    label: option.name,
    value: option.id,
    defaultBucketIndex: option.defaultBucket,
  }));

  const assetBucketOptions = getAssetBucketOptions({
    assetBucketOptions: baseAssetBucketOptions,
    assetBucketingByManagementStyle,
    managementStyleSelection,
    managementStylesOptions,
  });

  const onManagementSelect = baseOnManagementSelect({
    options: managementStylesOptions,
    setValue: (value: number) => formBag.setValue('customBucketIndex', value),
  });

  const resetValues = () => {
    formBag.reset({ ...dataToForm({ data: asset, options }) });
  };

  return {
    data: {
      formBag,
      mutationBag,
      ownerOptions,
      saleYearOptions,
      defaultShowAdvanced,
      assetBucketOptions,
      assetBucketingByManagementStyle,
      managementStylesOptions,
      onManagementSelect,
      allowAssetBucketingCustomization: options.AllowAssetBucketingCustomization,
      displayAssetBucketing,
    },
    actions: {
      onSubmit,
      resetValues,
    },
  };
};
